// Global alert service for non-React contexts
import { trackExceptionWithContext } from './application-insights/appInsights';
import { AppInsightsComponents, AppInsightsErrorTypes } from './application-insights/appInsightsConstants';

export interface IGlobalAlertService {
    showSuccess: (message: string, options?: { autoDismissTime?: number }) => void;
    showError: (message: string, options?: { autoDismissTime?: number }) => void;
    showWarning: (message: string, options?: { autoDismissTime?: number }) => void;
    showInfo: (message: string, options?: { autoDismissTime?: number }) => void;
}

let globalAlertService: IGlobalAlertService | null = null;

export const setGlobalAlertService = (service: IGlobalAlertService) => {
    try {
        // Validate service implementation
        if (!service || typeof service !== 'object') {
            throw new Error('Invalid service: service must be an object');
        }

        const requiredMethods = ['showSuccess', 'showError', 'showWarning', 'showInfo'];
        const missingMethods = requiredMethods.filter(method => typeof service[method as keyof IGlobalAlertService] !== 'function');
        
        if (missingMethods.length > 0) {
            throw new Error(`Invalid service: missing required methods: ${missingMethods.join(', ')}`);
        }

        globalAlertService = service;
    } catch (error) {
        trackExceptionWithContext(error as Error, {
            component: AppInsightsComponents.GLOBAL_ALERT_SERVICE,
            action: 'service-registration-failed',
            additionalData: {
                errorType: AppInsightsErrorTypes.CONFIGURATION_ERROR,
                hasService: !!service,
                serviceType: typeof service,
                serviceKeys: service ? Object.keys(service) : []
            }
        });
        throw error;
    }
};

// Export functions for use throughout the app
export const showSuccessAlert = (message: string, options?: { autoDismissTime?: number }) => {
    if (globalAlertService) {
        try {
            globalAlertService.showSuccess(message, options);
        } catch (error) {
            trackExceptionWithContext(error as Error, {
                component: AppInsightsComponents.GLOBAL_ALERT_SERVICE,
                action: 'show-success-failed',
                additionalData: {
                    errorType: AppInsightsErrorTypes.ALERT_SYSTEM_ERROR,
                    alertType: 'success',
                    messageLength: message?.length || 0,
                    hasOptions: !!options,
                    autoDismissTime: options?.autoDismissTime
                }
            });
            // console.log('Success:', message); // Fallback
        }
    } else {
        // Track when service is not available
        trackExceptionWithContext(new Error('Global alert service not available for success alert'), {
            component: AppInsightsComponents.GLOBAL_ALERT_SERVICE,
            action: 'service-unavailable',
            additionalData: {
                errorType: AppInsightsErrorTypes.ALERT_SERVICE_UNAVAILABLE_ERROR,
                alertType: 'success',
                messageLength: message?.length || 0,
                hasOptions: !!options
            }
        });
        // console.log('Success:', message);
    }
};

export const showErrorAlert = (message: string, options?: { autoDismissTime?: number }) => {
    if (globalAlertService) {
        try {
            globalAlertService.showError(message, options);
        } catch (error) {
            trackExceptionWithContext(error as Error, {
                component: AppInsightsComponents.GLOBAL_ALERT_SERVICE,
                action: 'show-error-failed',
                additionalData: {
                    errorType: AppInsightsErrorTypes.ALERT_SYSTEM_ERROR,
                    alertType: 'error',
                    messageLength: message?.length || 0,
                    hasOptions: !!options,
                    autoDismissTime: options?.autoDismissTime
                }
            });
            console.error('Error:', message); // Fallback
        }
    } else {
        // Track when service is not available
        trackExceptionWithContext(new Error('Global alert service not available for error alert'), {
            component: AppInsightsComponents.GLOBAL_ALERT_SERVICE,
            action: 'service-unavailable',
            additionalData: {
                errorType: AppInsightsErrorTypes.ALERT_SERVICE_UNAVAILABLE_ERROR,
                alertType: 'error',
                messageLength: message?.length || 0,
                hasOptions: !!options
            }
        });
        console.error('Error:', message);
    }
};

export const showWarningAlert = (message: string, options?: { autoDismissTime?: number }) => {
    if (globalAlertService) {
        try {
            globalAlertService.showWarning(message, options);
        } catch (error) {
            trackExceptionWithContext(error as Error, {
                component: AppInsightsComponents.GLOBAL_ALERT_SERVICE,
                action: 'show-warning-failed',
                additionalData: {
                    errorType: AppInsightsErrorTypes.ALERT_SYSTEM_ERROR,
                    alertType: 'warning',
                    messageLength: message?.length || 0,
                    hasOptions: !!options,
                    autoDismissTime: options?.autoDismissTime
                }
            });
            console.warn('Warning:', message); // Fallback
        }
    } else {
        // Track when service is not available
        trackExceptionWithContext(new Error('Global alert service not available for warning alert'), {
            component: AppInsightsComponents.GLOBAL_ALERT_SERVICE,
            action: 'service-unavailable',
            additionalData: {
                errorType: AppInsightsErrorTypes.ALERT_SERVICE_UNAVAILABLE_ERROR,
                alertType: 'warning',
                messageLength: message?.length || 0,
                hasOptions: !!options
            }
        });
        console.warn('Warning:', message);
    }
};

export const showInfoAlert = (message: string, options?: { autoDismissTime?: number }) => {
    if (globalAlertService) {
        try {
            globalAlertService.showInfo(message, options);
        } catch (error) {
            trackExceptionWithContext(error as Error, {
                component: AppInsightsComponents.GLOBAL_ALERT_SERVICE,
                action: 'show-info-failed',
                additionalData: {
                    errorType: AppInsightsErrorTypes.ALERT_SYSTEM_ERROR,
                    alertType: 'info',
                    messageLength: message?.length || 0,
                    hasOptions: !!options,
                    autoDismissTime: options?.autoDismissTime
                }
            });
            console.info('Info:', message); // Fallback
        }
    } else {
        // Track when service is not available
        trackExceptionWithContext(new Error('Global alert service not available for info alert'), {
            component: AppInsightsComponents.GLOBAL_ALERT_SERVICE,
            action: 'service-unavailable',
            additionalData: {
                errorType: AppInsightsErrorTypes.ALERT_SERVICE_UNAVAILABLE_ERROR,
                alertType: 'info',
                messageLength: message?.length || 0,
                hasOptions: !!options
            }
        });
        console.info('Info:', message);
    }
};
